/**
 * Leitura de uma seleção de arquivos preservando o caminho relativo (RN-M2-003).
 *
 * - Clique no seletor (`webkitdirectory`): o navegador já dá `webkitRelativePath`.
 * - Arrastar-e-soltar: `dataTransfer.files` só traz o primeiro nível, sem caminho;
 *   aqui cada item é lido por `webkitGetAsEntry()` e as pastas são percorridas
 *   recursivamente. O leitor de diretório do Chrome devolve no máximo 100 entradas
 *   por chamada — `readEntries` é repetido até vir vazio.
 */

/** Arquivo selecionado com o caminho relativo que deve ser preservado. */
export interface ArquivoSelecionado {
  readonly file: File;
  readonly caminho: string;
}

interface EntradaArquivo {
  readonly isFile: true;
  readonly isDirectory: false;
  readonly name: string;
  file(ok: (f: File) => void, erro?: (e: unknown) => void): void;
}

interface EntradaPasta {
  readonly isFile: false;
  readonly isDirectory: true;
  readonly name: string;
  createReader(): { readEntries(ok: (e: Entrada[]) => void, erro?: (e: unknown) => void): void };
}

type Entrada = EntradaArquivo | EntradaPasta;

function lerArquivo(entrada: EntradaArquivo): Promise<File> {
  return new Promise((ok, erro) => entrada.file(ok, erro));
}

async function lerTodasAsEntradas(pasta: EntradaPasta): Promise<Entrada[]> {
  const leitor = pasta.createReader();
  const todas: Entrada[] = [];
  for (;;) {
    const bloco = await new Promise<Entrada[]>((ok, erro) => leitor.readEntries(ok, erro));
    if (bloco.length === 0) {
      return todas;
    }
    todas.push(...bloco);
  }
}

async function percorrer(entrada: Entrada, prefixo: string, saida: ArquivoSelecionado[]): Promise<void> {
  const caminho = prefixo ? `${prefixo}/${entrada.name}` : entrada.name;
  if (entrada.isFile) {
    saida.push({ file: await lerArquivo(entrada), caminho });
    return;
  }
  for (const filho of await lerTodasAsEntradas(entrada)) {
    await percorrer(filho, caminho, saida);
  }
}

/** Arquivos (com caminho) de um drop — pastas percorridas recursivamente. */
export async function lerItensArrastados(itens: DataTransferItemList): Promise<ArquivoSelecionado[]> {
  // As entradas precisam ser obtidas de forma síncrona, antes do primeiro await:
  // o navegador invalida a lista de itens ao fim do evento de drop.
  const entradas: Entrada[] = [];
  for (let i = 0; i < itens.length; i += 1) {
    const item = itens[i];
    // O tipo do DOM (FileSystemEntry) não expõe os métodos por subtipo; usamos o nosso.
    const entrada = item.kind === 'file' ? (item.webkitGetAsEntry() as unknown as Entrada | null) : null;
    if (entrada) {
      entradas.push(entrada);
    }
  }
  const saida: ArquivoSelecionado[] = [];
  for (const entrada of entradas) {
    await percorrer(entrada, '', saida);
  }
  return saida;
}

/** Arquivos (com caminho) de um seletor `<input webkitdirectory>` ou múltiplo. */
export function selecionadosDoInput(files: readonly File[]): ArquivoSelecionado[] {
  return files.map((file) => {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
    return { file, caminho: (rel && rel.length > 0 ? rel : file.name).replace(/\\/g, '/') };
  });
}
